import { lazy, Suspense } from 'react';
import { QueryClientProvider } from '@tanstack/react-query';
import { HelmetProvider } from 'react-helmet-async';
import { HashRouter, Routes, Route, Navigate, Outlet } from 'react-router-dom';
import { queryClient } from './providers/query-client';
import ProtectedRoute from './components/routing/ProtectedRoute';
import SuspenseFallback from './components/ui/SuspenseFallback';
import MyRequisitionsHistoryPage from './pages/requests/MyRequisitionsHistoryPage';
// Start of lazy loaded components
const LoginPage = lazy(() => import('./pages/LoginPage'));
const ChangePasswordPage = lazy(() => import('./pages/ChangePasswordPage'));
const DashboardPage = lazy(() => import('./pages/DashboardPage'));
const ProjectsSettingsPage = lazy(() => import('./pages/settings/ProjectsSettingsPage'));
const UsersSettingsPage = lazy(() => import('./pages/settings/UsersSettingsPage'));
const WorkflowConfigPage = lazy(() => import('./pages/settings/WorkflowConfigPage'));
const LogFramePage = lazy(() => import('./pages/tools/LogFramePage'));
const ActivityPlanPage = lazy(() => import('./pages/tools/ActivityPlanPage'));
const CurrenciesPage = lazy(() => import('./pages/finances/CurrenciesPage'));
const ExchangeRatesPage = lazy(() => import('./pages/finances/ExchangeRatesPage'));
const BudgetPage = lazy(() => import('./pages/finances/BudgetPage'));
const CostCentersPage = lazy(() => import('./pages/finances/CostCentersPage'));
const ImprestPage = lazy(() => import('./pages/requests/ImprestPage'));
const RetirementPage = lazy(() => import('./pages/requests/RetirementPage'));
const PettyCashPage = lazy(() => import('./pages/requests/PettyCashPage'));
const PurchaseRequisitionPage = lazy(() => import('./pages/requests/PurchaseRequisitionPage'));
const ClaimPage = lazy(() => import('./pages/requests/ClaimPage'));
const LocalPurchaseOrderPage = lazy(() => import('./pages/requests/LocalPurchaseOrderPage'));
const PaymentVoucherPage = lazy(() => import('./pages/requests/PaymentVoucherPage'));
const PaymentsPage = lazy(() => import('./pages/requests/PaymentsPage'));
const ClientsPage = lazy(() => import('./pages/ClientsPage'));
const BudgetApprovalsPage = lazy(() => import('./pages/BudgetApprovalsPage'));
const DraftsPage = lazy(() => import('./pages/requests/DraftsPage'));
const MyRequisitionsPage = lazy(() => import('./pages/requests/MyRequisitionsPage'));
const ArchivedPage = lazy(() => import('./pages/requests/ArchivedPage'));
const ApprovalsPage = lazy(() => import('./pages/requests/ApprovalsPage'));

// End of lazy loaded components
/**
 * Improvement to be done to increase performance:
 * 1. Auth ProtectedRoutes 
 * 2. Lazy load components
 * 3. Grouupd dashboard routes
 * 4. Auth Loading splash screen
 * 5. Disable queries until authenticated
 */

const App = () => (
  <HelmetProvider>
    <QueryClientProvider client={queryClient}>
      <HashRouter>
        <Suspense fallback={<SuspenseFallback />}>
          <Routes>
            <Route path="/" element={<LoginPage />} />
            {/* <Route path="/" element={<SuspenseFallback />} /> */}
            <Route element={<ProtectedRoute />}>
              <Route path="/change-password" element={<ChangePasswordPage />} />

              <Route path="/dashboard" element={<Outlet />}>
                <Route index element={<DashboardPage />} />
                <Route
                  path="settings/projects"
                  element={<ProjectsSettingsPage />}
                />
                <Route path="settings/users" element={<UsersSettingsPage />} />
                <Route
                  path="settings/workflow-configs"
                  element={<WorkflowConfigPage />}
                />

                <Route path="tools/logframe" element={<LogFramePage />} />
                <Route
                  path="tools/activity-plan"
                  element={<ActivityPlanPage />}
                />

                <Route
                  path="finances/currencies"
                  element={<CurrenciesPage />}
                />
                <Route
                  path="finances/exchange-rates"
                  element={<ExchangeRatesPage />}
                />
                <Route path="finances/budget" element={<BudgetPage />} />
                <Route
                  path="finances/cost-centers"
                  element={<CostCentersPage />}
                />

                <Route path="requests/imprest" element={<ImprestPage />} />
                <Route path="requests/imprest/:id" element={<ImprestPage />} />
                <Route
                  path="requests/retirement"
                  element={<RetirementPage />}
                />
                <Route
                  path="requests/retirement/:id"
                  element={<RetirementPage />}
                />
                <Route path="requests/petty-cash" element={<PettyCashPage />} />
                <Route
                  path="requests/petty-cash/:id"
                  element={<PettyCashPage />}
                />
                <Route
                  path="requests/purchase-requisition"
                  element={<PurchaseRequisitionPage />}
                />
                <Route
                  path="requests/purchase-requisition/:id"
                  element={<PurchaseRequisitionPage />}
                />
                <Route path="requests/claim" element={<ClaimPage />} />
                <Route path="requests/claim/:id" element={<ClaimPage />} />
                <Route
                  path="requests/lpo"
                  element={<LocalPurchaseOrderPage />}
                />
                <Route
                  path="requests/lpo/:id"
                  element={<LocalPurchaseOrderPage />}
                />
                <Route
                  path="requests/payment-voucher"
                  element={<PaymentVoucherPage />}
                />
                <Route
                  path="requests/payment-voucher/:id"
                  element={<PaymentVoucherPage />}
                />
                <Route
                  path="requests/dashboard"
                  element={<Navigate to="/dashboard" replace />}
                />
                <Route path="requests/payments" element={<PaymentsPage />} />
                <Route path="requests/drafts" element={<DraftsPage />} />

                <Route
                  path="requisitions/my-requisitions"
                  element={<MyRequisitionsPage />}
                />
                <Route
                  path="requisitions/my-requisitions-history"
                  element={<MyRequisitionsHistoryPage />}
                />
                <Route
                  path="requisitions/approvals"
                  element={<ApprovalsPage />}
                />
                <Route
                  path="requisitions/archived"
                  element={<ArchivedPage />}
                />

                <Route path="clients" element={<ClientsPage />} />
                <Route
                  path="budget-approvals"
                  element={<BudgetApprovalsPage />}
                />
              </Route>
            </Route>

            <Route path="*" element={<Navigate to="/dashboard" replace />} />
          </Routes>
        </Suspense>
      </HashRouter>
    </QueryClientProvider>
  </HelmetProvider>
);

export default App;
